import DashboardDefaultChartEmptyState from "../../../components/EmptyStates/DashboardDefaultChartEmptyState";
import useCalendarActivityTypes from "../../../hooks/useCalendarActivityTypes";
import useScreenBreakpoint from "../../../hooks/useScreenBreakpoint";
import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import {
  hasDoneActivitiesByMonthStackedBarChartAccessMaximumRange,
  hasDoneActivitiesByMonthStackedBarChartAccessMediumRange,
} from "../../../selectors/powersSelectors";
import { sendEventChartUsed } from "../../../services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "../../../utils/constants/dashboard/analyticDashboardEvents";
import { DASHBOARD_CHART_TYPES } from "../../../utils/constants/dashboard/dashboardModuleConstants";

import DashboardChartWrapper from "../../../components/DashboardChartWrapper";
import { QueryLoaderWrapper, Skeletons } from "../../../components/Loaders";
import { CustomChartStyleWrapper, ToggletipText } from "../../../style";
import { getChartHeightByPowers, getChartOptions } from "./helpers";
import useDoneActivitiesByMonthChartQuery from "./useDoneActivitiesByMonthStackedBarChartQuery";
import ChartActions from "@components/ChartActions/ChartActions";

const DoneActivitiesByMonthChart = () => {
  const hasMediumRangePowers = useSelector(
    hasDoneActivitiesByMonthStackedBarChartAccessMediumRange
  );
  const hasMaximumRangePowers = useSelector(
    hasDoneActivitiesByMonthStackedBarChartAccessMaximumRange
  );

  const { isMobileScreen } = useScreenBreakpoint();

  const CHART_HEIGHT = getChartHeightByPowers(
    hasMediumRangePowers,
    hasMaximumRangePowers,
    isMobileScreen
  );

  const monthLengthByPowers = useMemo(() => {
    if (hasMaximumRangePowers) return 12;
    if (hasMediumRangePowers) return 6;
    return 3;
  }, [hasMaximumRangePowers, hasMediumRangePowers]);

  const calendarActivityTypes = useCalendarActivityTypes();
  const [queryStatus, doneActivitiesResponse, fetchedTime, refetch] =
    useDoneActivitiesByMonthChartQuery(monthLengthByPowers, calendarActivityTypes);

  const { months, series, noData } = doneActivitiesResponse;

  const options = getChartOptions(months, calendarActivityTypes);

  const shouldRenderEmptyState = noData;

  const header = {
    title: "Atividades concluídas de sua responsabilidade",
    description: `Confira todas as atividades concluídas que você era responsável nos últimos ${monthLengthByPowers} meses para avaliar sua produtividade.`,
  };

  const toggleTip = {
    title: "Atividades concluídas",
    text: (
      <ToggletipText>
        No gráfico, são consideradas as atividades em que você é responsável na data em que deveriam
        ser concluídas.
      </ToggletipText>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_MONTH,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_MONTH,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043386-como-analisar-as-suas-atividades-concluidas",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <ChartActions updatedAt={fetchedTime} refetchFunction={refetch} />
      {shouldRenderEmptyState ? (
        <DashboardDefaultChartEmptyState
          title="Não há dados suficientes para gerar o gráfico"
          chartId={DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_MONTH}
          subtitle="Quando você for responsável por alguma atividade concluída, o Astrea mostrará o gráfico aqui."
          link="https://astrea.aurum.com.br/pt-BR/articles/8043386-como-analisar-as-suas-atividades-concluidas"
        />
      ) : (
        <CustomChartStyleWrapper>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            spinnerConfig={{ height: CHART_HEIGHT }}
            skeletonComponent={<Skeletons.DoneActivitiesByMonthSkeleton height={CHART_HEIGHT} />}
          >
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              width="95%"
              height={CHART_HEIGHT}
            />
          </QueryLoaderWrapper>
        </CustomChartStyleWrapper>
      )}
    </DashboardChartWrapper>
  );
};

export default DoneActivitiesByMonthChart;
